import { Button, Card, Checkbox, Col, Divider, Flex, Form, FormProps, Radio, RadioChangeEvent, Row, Select, TimePicker, Typography } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useWatch } from 'antd/es/form/Form';
import dayjs from 'dayjs';

//components
import ComponentHeader from 'components/componentHeader/componentHeader';
import DatePickerElement from 'components/datePicker/datePicker';
import MapComponent from 'components/map/map';
import FormElement from 'components/formElement/formElement';
import MeetingGuests from '../components/meetingGuests';
import FormListFilesElement from 'components/documentsListForm/documentsListForm';
import BoardDecisionElement from '../components/decisions';
import BackButton from 'components/backButton/backButton';
import PageTitle from 'components/title/title';

// services
import NotificationService from 'core/services/notification';
import { mapResponseErrors } from 'utils/mapResponseErrors';
import { onCreateMeeting, onGetMeetingById, onUpdateMeeting } from '../services/meetingServices';

//models
import { BoardDecisionFormItem, CreateMeetingPayload, Meeting, MeetingGuest, MeetingLocation } from '../models/meetings';

//styles
import googleMeetLogo from 'assets/icons/google_meet.svg';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import { StatusColors } from 'utils/statusColors';
import StatusComponent from 'components/status/status';
import { onGetActiveBoard } from '../services/boardServices';
import { Board } from '../models/board';

interface MeetingEditPageProps {}

const MeetingEditPage: FunctionComponent<MeetingEditPageProps> = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { id } = useParams<{ id: string }>();

	const [meetingForm] = Form.useForm();
	const meetingTypeWatcher = useWatch('type', meetingForm);

	const [boardDetails, setBoardDetails] = useState<Board>();

	const [meeting, setMeeting] = useState<Meeting>();
	const [guests, setGuests] = useState<MeetingGuest[]>([]);
	const [meetingLocation, setMeetingLocation] = useState<MeetingLocation[]>(['Online']);
	const [deletedDecisions, setDeletedDecisions] = useState<number[]>([]);

	const meetingTypeWatch = useWatch('type', meetingForm);

	const [isLoading, setIsLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const meetingTypeOptions = [
		{
			value: 'Scheduled',
			label: (
				<>
					<p className="mb-0">{t('ACTIONS.SCHEDULED')}</p>
					<p className="mb-0 text-light">{t('ACTIONS.FOR_SCHEDULED_MEETINGS')}</p>
				</>
			),
		},
		{
			value: 'Urgent',
			label: (
				<>
					<p className="mb-0">{t('ACTIONS.URGENT')}</p>
					<p className="mb-0 text-light">{t('ACTIONS.IMMEDIATE_ACTION_NEEDED')}</p>
				</>
			),
		},
		{
			value: 'Voting',
			label: (
				<>
					<p className="mb-0">{t('ACTIONS.VOTING')}</p>
					<p className="mb-0 text-light">{t('ACTIONS.NO_DELIBERATION_VOTES')}</p>
				</>
			),
		},
	].filter((option) => option.value !== 'Voting' || boardDetails?.allow_swipe_voting || meeting?.type === 'Voting');

	useEffect(() => {
		getData();
	}, []);

	const getData = async () => {
		try {
			setIsLoading(true);
			await Promise.all([getMeetingDetails(), getActiveBoard()]);
			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('ACTION.FETCH_ERROR'));
			navigate('/dashboard/actions/boards/meetings');
		}
	};

	const getActiveBoard = async () => {
		const response = await onGetActiveBoard();
		setBoardDetails(response);
	};

	const getMeetingDetails = async () => {
		const data = await onGetMeetingById(id!);

		meetingForm.setFieldsValue({
			...data,
			date: dayjs(data.date),
			decisions: data.decisions.map((decision) => ({ ...decision, ar: decision.translated_title.ar, en: decision.translated_title.en })),
		});
		setMeetingLocation(data.location_type === 'Both' ? ['Online', 'Physical'] : [data.location_type]);
		setGuests(data.guests || []);
		setMeeting(data);
	};

	const handleTypeChange = (event: RadioChangeEvent) => {
		if (event?.target.value == 'Voting' && meetingForm.getFieldValue('decisions')?.length == 0) {
			meetingForm.setFieldValue('decisions', [{ ar: '', en: '' }]);
		}
	};

	const handleLocationChange = (location: MeetingLocation, checked: boolean) => {
		if (checked) {
			setMeetingLocation([...meetingLocation, location]);
			meetingForm.setFieldsValue({ location_type: [...meetingLocation, location] });
		} else {
			const tempLocation = [...meetingLocation];
			tempLocation.splice(tempLocation.indexOf(location), 1);
			setMeetingLocation(tempLocation);
			meetingForm.setFieldsValue({ location_type: tempLocation });
		}
	};

	const onFinish: FormProps<CreateMeetingPayload>['onFinish'] = async (values) => {
		try {
			setIsSubmitting(true);

			const payload = new FormData();

			payload.append('_method', 'PUT');
			payload.append('type', values.type);
			payload.append('date', dayjs(values.date).format('YYYY-MM-DD hh:mm a'));
			payload.append('location_type', meetingLocation.length == 2 ? 'Both' : meetingLocation[0]);

			values.agendas?.forEach((fileObj, index: number) => {
				payload.append(`agendas[${index}][id]`, fileObj.id!);
				payload.append(`agendas[${index}][title]`, fileObj.title);
				if (fileObj.file) payload.append(`agendas[${index}][file]`, fileObj.file as Blob);
			});

			values.decisions.forEach((point: BoardDecisionFormItem, index: number) => {
				if (point.id) payload.append(`decisions[${index}][id]`, point.id);
				payload.append(`decisions[${index}][ar]`, point.ar ?? '');
				payload.append(`decisions[${index}][en]`, point.en ?? '');
				payload.append(`decisions[${index}][abstention_type]`, point.abstention_type);

				if (point.abstention_type === 'specific_board_members')
					point.abstaining_board_member_ids?.forEach((shareholder_id: string, shIndex: number) => {
						payload.append(`decisions[${index}][abstaining_board_member_ids][${shIndex}]`, shareholder_id);
					});
			});

			deletedDecisions.forEach((decisionId, index) => {
				payload.append(`deleted_decision_ids[${index}]`, decisionId.toString());
			});

			if (meetingLocation.includes('Physical')) {
				payload.append('physical_lat', values.physical_lat!);
				payload.append('physical_lng', values.physical_lng!);
				payload.append('physical_address', values.physical_address!);
				payload.append('physical_address_description', values.physical_address_description!);
			}

			if (values?.type === 'Voting' && values?.duration_minutes) {
				const totalMinutes = dayjs(values.duration_minutes).hour() * 60 + dayjs(values.duration_minutes).minute();
				payload.append('duration_minutes', totalMinutes.toString());
			}

			if (values?.type != 'Voting' && guests && guests.length > 0) {
				guests.forEach((guest, index) => {
					payload.append(`guests[${index}][name]`, guest.name);
					payload.append(`guests[${index}][name_en]`, guest.name_en);
					payload.append(`guests[${index}][email]`, guest.email);
					payload.append(`guests[${index}][phone_number]`, guest.phone_number);
					payload.append(`guests[${index}][phone_country_code]`, guest.phone_country_code);
					payload.append(`guests[${index}][position]`, guest.position);
					payload.append(`guests[${index}][organization]`, guest.organization);
					payload.append(`guests[${index}][sector]`, guest.sector);
				});
			}

			await onUpdateMeeting(id!, payload);

			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.MEETING_UPDATED_SUCCESSFULLY'));

			navigate('/dashboard/actions/boards/meetings');
		} catch (error: any) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.ERROR'), error?.response?.data?.message || t('COMMON.SUBMIT_ERROR'));
			if (error.status === 422) mapResponseErrors(error, meetingForm);
		}
	};

	return (
		<>
			<PageTitle name={t('PAGES.BOARD_MEETING')} />

			<BackButton />

			<Card>
				{isLoading ? (
					<LoadingComponent />
				) : (
					<>
						<div>
							<Flex gap={20} align="center" className="mb-2">
								<h5 style={{ fontWeight: 500, marginBottom: 0 }}>#{meeting?.id}</h5>
								<StatusComponent text={t(`COMMON.${meeting?.status.toUpperCase()}`)} small color={StatusColors[meeting?.status as string]} />
							</Flex>
							<p style={{ color: '#555' }}>{meeting?.created_at}</p>
						</div>

						<Form
							form={meetingForm}
							onFinish={onFinish}
							className="mt-5"
							initialValues={{ type: 'Scheduled', agendas: [{}], decisions: [{}], voting_duration: dayjs('01:00', 'HH:mm') }}>
							<Row gutter={[16, 16]}>
								<Col span={24} className="mb-3">
									<Flex gap={20}>
										<h6 className="mb-3 fw-medium">{t('ACTIONS.MEETING_TYPE')}:</h6>
										<Form.Item name="type" style={{ maxWidth: '100%' }}>
											<Radio.Group
												style={{ display: 'flex', justifyContent: 'space-between', gap: 20 }}
												options={meetingTypeOptions}
												onChange={handleTypeChange}
											/>
										</Form.Item>
									</Flex>
								</Col>

								<Col span={24} md={12}>
									<DatePickerElement
										name="date"
										rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
										label={t('ACTIONS.CHOOSE_MEETING_TIME')}
										minDate={
											meetingTypeWatcher == 'Urgent'
												? dayjs().subtract(1, 'day')
												: dayjs().add(boardDetails?.minimum_notification_days_before_meetings || 1, 'day')
										}
										showTime={{ format: 'hh:mm a' }}
									/>
								</Col>

								<Col span={24} md={12}>
									{meetingTypeWatch === 'Voting' ? (
										<>
											<Typography.Text className="d-block" style={{ marginBottom: 5, display: 'block', fontSize: 15, textAlign: 'start' }}>
												{t('ACTIONS.VOTING_DURATION')}
											</Typography.Text>
											<Form.Item name="duration_minutes" rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}>
												<TimePicker
													format="HH:mm"
													showNow={false}
													needConfirm={false}
													style={{ width: '100%' }}
													onChange={(time) => meetingForm.setFieldsValue({ duration_minutes: time })}
												/>
											</Form.Item>
										</>
									) : (
										<div>
											<h6 className="mb-3">{t('ACTIONS.LOCATION')}</h6>
											<Flex gap={10} wrap>
												<div>
													<Checkbox
														defaultChecked={meetingLocation.includes('Online')}
														className="compoundCheckbox"
														onChange={(e) => handleLocationChange('Online', e.target.checked)}>
														<Flex align="center" gap={5}>
															<p className="fw-medium mb-0">{t('ACTIONS.ONLINE')} - </p> <img src={googleMeetLogo} style={{ height: 25 }} />
														</Flex>
													</Checkbox>
												</div>
												<div>
													<Checkbox
														defaultChecked={meetingLocation.includes('Physical')}
														className="compoundCheckbox"
														onChange={(e) => handleLocationChange('Physical', e.target.checked)}>
														<div>
															<p className="fw-medium mb-0">{t('ACTIONS.PHYSICAL')} </p>
														</div>
													</Checkbox>
												</div>
											</Flex>
											<Form.Item
												name="location_type"
												hidden
												noStyle
												initialValue={meetingLocation}
												rules={[{ required: true, message: t('ACTIONS.SELECT_AT_LEAST_ONE_LOCATION') }]}
											/>
										</div>
									)}
								</Col>

								{meetingTypeWatch != 'Voting' && !meetingLocation.includes('Physical') && <Divider />}

								{meetingTypeWatch != 'Voting' && meetingLocation.includes('Physical') && (
									<Col span={24}>
										<div className="my-4">
											<MapComponent
												lat={+meetingForm.getFieldValue('physical_lat')}
												lng={+meetingForm.getFieldValue('physical_lng')}
												onSelect={(lat, lng, address) => {
													meetingForm.setFieldsValue({ physical_lat: lat, physical_lng: lng, physical_address: address });
												}}
											/>

											<div className="mt-3">
												<Form.Item name="physical_lat" hidden noStyle rules={[{ required: meetingLocation.includes('Physical') }]} />
												<Form.Item name="physical_lng" hidden noStyle rules={[{ required: meetingLocation.includes('Physical') }]} />

												<FormElement
													fullWidth
													name="physical_address"
													label={t('ACTIONS.ADDRESS')}
													type="text"
													rules={[{ required: meetingLocation.includes('Physical'), message: t('COMMON.FIELD_REQUIRED') }]}
												/>

												<FormElement
													fullWidth
													name="physical_address_description"
													label={t('ACTIONS.ADDRESS_DESC')}
													type="text"
													rules={[{ required: meetingLocation.includes('Physical'), message: t('COMMON.FIELD_REQUIRED') }]}
												/>
											</div>
										</div>

										<Divider />
									</Col>
								)}

								{meetingTypeWatch !== 'Voting' && boardDetails?.allow_external_guest_invitation && (
									<Col span={24}>
										<MeetingGuests onChange={setGuests} guests={guests} />

										<Divider />
									</Col>
								)}

								<Col span={24}>
									<h6 className="mb-3" style={{ fontSize: '1.1rem' }}>
										{t('ACTIONS.MEETING_AGENDA')}
									</h6>

									<FormListFilesElement form={meetingForm} fieldName="agendas" editMode />
								</Col>

								<Divider />

								<Col span={24}>
									<BoardDecisionElement
										type={meetingTypeWatch}
										form={meetingForm}
										decisions={meeting?.decisions}
										onDeleteDecision={(decisionId) => setDeletedDecisions((prev) => [...prev, decisionId])}
									/>
								</Col>

								<Col span={24} className="mt-5">
									<Flex justify="end">
										<Button type="primary" htmlType="submit" loading={isSubmitting}>
											{t('COMMON.UPDATE')}
										</Button>
									</Flex>
								</Col>
							</Row>
						</Form>
					</>
				)}
			</Card>
		</>
	);
};

export default MeetingEditPage;
